/**
 * @file AgencyProPreview.jsx
 * @description "What Pro adds" on the agency landing page, under the plan cards: the Pro screens (Performance and Market
 *   Reach as real screenshots, plus a drawn Team logins sample), all with sample data, in the same frame as the product
 *   showcase (ProductShowcase, light tone) so they stay readable. No button: the plan cards above carry the page's
 *   call to action. Images live in public/marketing/agency (WebP, 2x).
 * @module components/AgencyProPreview
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ProductShowcase from "./ProductShowcase";
import ProTeamSample from "./ProTeamSample";
import { TrendingUpIcon, MapPinIcon, UsersIcon } from "./Icons";
import { getAgencyPricing } from "../lib/agencyPricing";
import { isWorkforceNamed } from "../lib/entitlements";

/**
 * The Pro screens, in order: Performance, Market Reach, Team logins (a drawn sample, not a screenshot).
 * @param {number} teamLogins logins Pro includes, the owner counted (platform_config via lib/agencyPricing)
 */
export function buildProTabs(teamLogins) {
  return [
    { id: "perf", key: "perf", Icon: TrendingUpIcon, src: "/marketing/agency/pro-performance.webp", w: 1440, h: 900, fit: "cover", badge: "PRO",
      labelKey: "agencyLanding.proPreview.perfTitle", captionKey: "agencyLanding.proPreview.perfDesc", altKey: "agencyLanding.proPreview.perfAlt" },
    { id: "reach", key: "reach", Icon: MapPinIcon, src: "/marketing/agency/pro-market-reach.webp", w: 1440, h: 900, fit: "cover", badge: "PRO",
      labelKey: "agencyLanding.proPreview.reachTitle", captionKey: "agencyLanding.proPreview.reachDesc", altKey: "agencyLanding.proPreview.reachAlt" },
    { id: "team", key: "team", Icon: UsersIcon, badge: "PRO", vars: { count: teamLogins },
      Panel: (props) => <ProTeamSample limit={teamLogins} {...props} />,
      labelKey: "agencyLanding.proPreview.teamTitle", captionKey: "agencyLanding.proPreview.teamDesc", altKey: "agencyLanding.proPreview.teamAlt" },
  ];
}

/** @param {{ isMobile: boolean, teamLogins?: number }} props teamLogins: the page's own live value; defaults to lib/agencyPricing */
export default function AgencyProPreview({ isMobile, teamLogins }) {
  const { t } = useTranslation();
  const pricing = getAgencyPricing();
  const logins = teamLogins ?? pricing.teamLogins;
  const tabs = useMemo(() => buildProTabs(logins), [logins]);
  return (
    <div data-testid="pro-preview" style={{ maxWidth: 1020, margin: isMobile ? "40px auto 0" : "56px auto 0" }}>
      <h3 style={{ textAlign: "center", fontSize: isMobile ? 20 : 22, fontWeight: 800, color: "#0F1024", margin: "0 0 18px", letterSpacing: "-0.01em" }}>
        {t("agencyLanding.proPreviewTitle")}
      </h3>
      {/* Workforce roster limit per plan: shown only once ABAcases Workforce is released (one switch: workforce_enabled). */}
      {isWorkforceNamed() && (
        <p data-testid="pro-workforce-point" style={{ textAlign: "center", fontSize: 14, color: "#475569", lineHeight: 1.5, margin: "0 0 16px" }}>
          {t("agencyLanding.proPreview.workforcePoint", { pro: pricing.workforceMaxPro, starter: pricing.workforceMaxStarter })}
        </p>
      )}
      <ProductShowcase isMobile={isMobile} tabs={tabs} tone="light" sampleTag testId="pro-preview" />
    </div>
  );
}
